import React from "react";
import { useTranslation } from "react-i18next";
import { TransformedProduct } from "../../../types/productApiTypes";
import WishlistToggleButton from "../../../../wishlist/components/common/WishlistToggleButton";
import CompareToggleButton from "../../../../compare/components/common/CompareToggleButton";

// Import the sub-components
import CardImage from "./CardImage";
import CardBadges from "./CardBadges";
import CardQuickActions from "./CardQuickActions";
import CardContent from "./CardContent";
import CardActions from "./CardActions";

// --- Component Props ---
interface ProductCardProps {
  product: TransformedProduct;
  view: "grid" | "list";
  onQuickViewClick: (productId: number) => void;
}

// --- Main Component ---
const ProductCard: React.FC<ProductCardProps> = ({
  product,
  view,
  onQuickViewClick,
}) => {
  const { ready: areTranslationsReady } = useTranslation();
  const rootClassName = `product-card product-card--${view}`;

  // Don't render the card until translations are ready
  // Loading states are handled by the parent component (ProductsGrid)
  if (!areTranslationsReady) {
    return null;
  }

  return (
    <div className={rootClassName}>
      <div className="product-card__image-wrapper">
        <CardImage product={product} />
        <CardBadges product={product} />

        {/* Mobile-first toggle buttons (Wishlist & Compare) */}
        <div className="product-card__mobile-actions">
          <WishlistToggleButton
            productId={product.id}
            className="product-card__toggle-btn"
          />
          <CompareToggleButton
            // Use the main product ID (Parent ID for variable products) for comparison.
            productId={product.id}
            className="product-card__toggle-btn"
          />
        </div>

        {/* The desktop hover quick actions */}
        <CardQuickActions
          product={product}
          onQuickViewClick={onQuickViewClick}
        />
      </div>
      <div className="product-card__content">
        <CardContent product={product} view={view} />

        {/* Hide the 'View Product' button in ProductCard context */}
        <CardActions
          product={product}
          onQuickViewClick={onQuickViewClick}
          showQuickViewButton={false}
        />
      </div>
    </div>
  );
};

export default ProductCard;
